import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-12" data-testid="forgot-root">
      <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-3">/ PASSWORD RESET</div>
      <h1 className="font-heading text-4xl font-black tracking-tighter uppercase mb-3">Forgot password</h1>
      <p className="text-sm text-gray-400 mb-8">
        Enter the email on your LegacyHLL account. We'll send a reset link that stays live for 60 minutes.
      </p>

      {sent ? (
        <div
          data-testid="forgot-success"
          className="border border-emerald-400/40 bg-emerald-400/5 p-6 space-y-3"
        >
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-xs font-mono uppercase tracking-widest font-bold">Reset link sent</span>
          </div>
          <p className="text-sm text-gray-300">
            If <span className="text-cyan-400 font-mono">{email}</span> is registered, a reset email is on its way. Check your inbox (and spam) for a message from <span className="text-cyan-400">LegacyHLL</span>. The link expires in 60 minutes.
          </p>
          <div className="flex gap-3 pt-2">
            <Link
              to="/login"
              data-testid="forgot-back-login"
              className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-3 py-1.5 font-bold"
            >
              Back to log in
            </Link>
            <button
              onClick={() => { setSent(false); setEmail(""); }}
              data-testid="forgot-send-again"
              className="text-[10px] font-mono uppercase tracking-widest text-gray-400 border border-[#232330] hover:border-white hover:text-white px-3 py-1.5"
            >
              Send another
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5 border border-[#232330] bg-[#101015] p-6">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-gray-400">Email</Label>
            <Input
              data-testid="forgot-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="rounded-none bg-[#05050A] border-[#232330] focus:border-cyan-400 focus:ring-cyan-400/40"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="forgot-submit"
            className="w-full rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black font-bold uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Mail className="w-4 h-4" /> {busy ? "Sending…" : "Send reset link"}
          </Button>
          <div className="text-sm text-gray-500 text-center">
            Remembered it?{" "}
            <Link to="/login" className="text-cyan-400 hover:underline" data-testid="forgot-login-link">
              Log in
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
